import { gql } from "@apollo/client";
import { DefaultFailureTypesIds, MEASURE_METRICS } from "constants/constants";

export const getEventReportsWithSecondaryCodesBO = gql`
    query getEventReportsWithSecondaryCodes(
        $whereEvents: event_report_bool_exp
        $limit: Int!
        $offset: Int!
        $order_by: [event_report_order_by!]
    ) {
        event_report(limit: $limit, offset: $offset, order_by: $order_by, where: $whereEvents) {
            event_report_id
            start_date
            end_date
            valid_time
            machine_center {
                machine_center_id
                short_name
                name
            }
            area {
                area_id
                translation {
                    id
                    translation_names {
                        name
                        language_id
                    }
                }
            }
            functional_location {
                functional_location_id
                translation {
                    id
                    translation_names {
                        name
                        language_id
                    }
                }
            }
            secondary_failure_code {
                failure_code_id
                code
                name
                default_failure_code_translations {
                    failure_code_id
                    language {
                        language_id
                        name
                    }
                    translated_name
                }
            }
            failure_type {
                failure_type_id
                name
                is_location_specific
                default_failure_type_translations {
                    failure_type_id
                    language {
                        language_id
                        name
                    }
                    translated_name
                }
            }
            failure_code {
                failure_code_id
                name
                default_failure_code_translations {
                    failure_code_id
                    language {
                        language_id
                        name
                    }
                    translated_name
                }
                secondary_failure_codes {
                    failure_code_id
                    name
                    default_failure_code_translations {
                        failure_code_id
                        language {
                            language_id
                            name
                        }
                        translated_name
                    }
                }
            }
        }
    }
`;

export const getLocationSpecificFailureCodes = gql`
    query getLocationSpecificFailureCodes(
        $functionalLocationId: Int
        $machineCenterId: Int!
        $isLocationSpecific: Boolean!
    ) {
        location_specific_functional_location_failure_code(
            where: {
                failure_code: {
                    primary_code_id: { _is_null: true }
                    deleted_at: { _is_null: true }
                    failure_type: { deleted_at: { _is_null: true } }
                }
                functional_location_id: { _eq: $functionalLocationId }
                functional_location: { disabled_at: { _is_null: true }, area: { disabled_at: { _is_null: true } } }
            }
        ) @include(if: $isLocationSpecific) {
            functional_location_id
            failure_code {
                failure_code_id
                failure_type_id
                name
                primary_code_id
                default_failure_code_translations {
                    failure_code_id
                    language {
                        language_id
                        name
                    }
                    translated_name
                }
            }
        }
        non_location_specific_failure_codes: non_location_specific_machine_center_failure_code(
            where: {
                failure_code: {
                    primary_code_id: { _is_null: true }
                    deleted_at: { _is_null: true }
                    failure_type: { deleted_at: { _is_null: true } }
                }
                machine_center_id: { _eq: $machineCenterId }
            }
        ) {
            failure_code {
                failure_code_id
                name
                primary_code_id
                failure_type_id
                default_failure_code_translations {
                    failure_code_id
                    language {
                        language_id
                        name
                    }
                    translated_name
                }
            }
        }
    }
`;

export const getFailureTypesOptions = gql`
    query getFailureTypesOptions($machineCenterId: Int!) {
        failure_type(
            where: {
                deleted_at: {_is_null: true},
                _or: [
                    { failure_type_id: { _eq: ${DefaultFailureTypesIds.BREAK_END_OF_SHIFT} } }
                    {
                        failure_codes: {
                            _or: [
                                {
                                    location_specific_functional_location_failure_codes: {
                                        functional_location: { area: { machine_center_id: { _eq: $machineCenterId }, disabled_at: {_is_null: true} }, disabled_at: {_is_null: true} }
                                    }
                                }
                                {
                                    non_location_specific_machine_center_failure_codes: {
                                        machine_center_id: { _eq: $machineCenterId }
                                    }
                                }
                            ]
                        }
                    }
                ]
            }
        ) {
            failure_type_id
            name
            is_location_specific
            default_failure_type_translations {
                failure_type_id
                language {
                    language_id
                    name
                }
                translated_name
            }
        }
    }
`;

export const getSecondaryFailureCodesOptions = gql`
    query getSecondaryFailureCodesOptions($where: failure_code_bool_exp) {
        failure_code(where: $where) {
            failure_code_id
            failure_type_id
            name
            primary_code_id
            default_failure_code_translations {
                failure_code_id
                language {
                    language_id
                    name
                }
                translated_name
            }
        }
    }
`;

export const getEventReportsWithSecondaryCodesIdsBO = gql`
    query getEventReportsWithSecondaryCodesIdsBO($whereEvents: event_report_bool_exp) {
        event_report(where: $whereEvents) {
            event_report_id
        }
    }
`;

export const getPendingEventReportsCountBO = gql`
    query getPendingEventReportsCountBO($where: event_report_bool_exp) {
        event_report_aggregate(where: $where) {
            aggregate {
                count
            }
        }
    }
`;

export const updateEventReportBO = gql`
    mutation updateEventReport($eventReportId: Int!, $changes: event_report_set_input!) {
        update_event_report(where: { event_report_id: { _eq: $eventReportId } }, _set: $changes) {
            affected_rows
            returning {
                event_report_id
            }
        }
    }
`;

// Built dynamically so each segment is inserted as its OWN statement (aliased
// insert_event_report_one) within the single transaction Hasura wraps around all
// root fields of one mutation. Separate statements — rather than one multi-row
// insert(objects) — guarantee each segment's BEFORE INSERT trigger sees the
// previously-inserted segments (Postgres read-your-writes between statements), so the
// auto-close trigger chains the end_dates correctly. Segments are supplied as
// $seg0..$seg{n-1} in ascending start order.
export const buildSplitMutationDocument = (segmentCount: number) => {
    const variableDefinitions = Array.from(
        { length: segmentCount },
        (_, index) => `$seg${index}: event_report_insert_input!`,
    ).join(", ");
    const insertFields = Array.from(
        { length: segmentCount },
        (_, index) => `seg${index}: insert_event_report_one(object: $seg${index}) { event_report_id }`,
    ).join("\n        ");

    const document = `
        mutation splitEventReport($eventReportId: Int!, $originalChanges: event_report_set_input!${
            segmentCount ? `, ${variableDefinitions}` : ""
        }) {
            update_event_report(where: { event_report_id: { _eq: $eventReportId } }, _set: $originalChanges) {
                affected_rows
            }
            ${insertFields}
        }
    `;

    return gql(document);
};

export const getEventReportsDetailsBO = gql`
    query getEventReportsDetailsBO(
        $where: event_report_bool_exp!
        $whereDowntimeDetails: event_report_downtime_details_bool_exp!
        $limit: Int!
        $offset: Int!
        $order_by: [event_report_order_by!]
        $order_by_downtime_details: [event_report_downtime_details_order_by!]
    ) {
        event_report(limit: $limit, offset: $offset, order_by: $order_by, where: $where) {
            event_report_id
            start_date
            end_date
            area_id
            cluster_id
            machine_center_id
            downtime_method_capture_id
            secondary_failure_code_id
            secondary_functional_location_id
            event_report_downtime_details(where: $whereDowntimeDetails, order_by: $order_by_downtime_details) {
                valid_time
                start_date
                end_date
                shift_day {
                    shift_historic {
                        shift_historic_id
                        start_time
                        end_time
                        shift {
                            shift_id
                            name
                        }
                        shift_days {
                            day {
                                day_id
                                name
                                default_day_translations {
                                    default_day_translation_id
                                    language {
                                        language_id
                                        name
                                    }
                                    translated_name
                                }
                            }
                            is_enabled
                        }
                    }
                }
            }
            machine_center {
                machine_center_id
                short_name
                name
                production_line {
                    name
                }
            }
            notes
            rca_event_report {
                rca_id
            }
            user_operator {
                user_id
                first_name
                last_name
                code
            }
            area {
                area_id
                translation {
                    id
                    translation_names {
                        name
                        language_id
                    }
                }
            }
            functional_location {
                functional_location_id
                translation {
                    id
                    translation_names {
                        name
                        language_id
                    }
                }
            }
            failure_type {
                failure_type_id
                name
                code
                is_location_specific
                default_failure_type_translations {
                    failure_type_id
                    language {
                        language_id
                        name
                    }
                    translated_name
                }
            }
            failure_code {
                failure_code_id
                name
                default_failure_code_translations {
                    failure_code_id
                    language {
                        language_id
                        name
                    }
                    translated_name
                }
            }
        }
    }
`;

export const getFunctionalLocations = gql`
    query getFunctionalLocations($areaId: Int!, $failureTypeId: Int!) {
        functional_location(
            where: {
                disabled_at: { _is_null: true }
                area_id: { _eq: $areaId }
                location_specific_functional_location_failure_codes: {
                    failure_code: { failure_type_id: { _eq: $failureTypeId } }
                }
                area: { disabled_at: { _is_null: true } }
            }
        ) {
            functional_location_id
            translation {
                id
                translation_names {
                    name
                    language_id
                }
            }
        }
    }
`;

export const getAreas = gql`
    query getAreas($machineCenterId: Int!, $failureTypeId: Int!) {
        area(
            where: {
                disabled_at: { _is_null: true }
                machine_center_id: { _eq: $machineCenterId }
                functional_locations: {
                    location_specific_functional_location_failure_codes: {
                        failure_code: { failure_type_id: { _eq: $failureTypeId } }
                    }
                    disabled_at: { _is_null: true }
                }
            }
        ) {
            area_id
            translation {
                id
                translation_names {
                    name
                    language_id
                }
            }
        }
    }
`;

export const getOperatorsForEventEdition = gql`
    query getOperatorsForEventEdition($machineCenterId: Int!, $clusterId: Int, $isCluster: Boolean!) {
        user_machine_center(where: { is_enabled: { _eq: true }, machine_center_id: { _eq: $machineCenterId } })
            @skip(if: $isCluster) {
            user {
                user_id
                first_name
                last_name
            }
        }
        user_cluster(where: { is_enabled: { _eq: true }, cluster_id: { _eq: $clusterId } }) @include(if: $isCluster) {
            user {
                user_id
                first_name
                last_name
            }
        }
    }
`;

export const getEventReportsDetailsTotalCountBO = gql`
    query getEventReportsDetailsTotalCountBO($where: event_report_bool_exp!) {
        total_count: event_report_aggregate(where: $where) {
            aggregate {
                count
            }
        }
    }
`;

export const getOngoingDowntimeEvent = gql`
query getOngoingDowntimeEvent ($machineCenterId: Int!) {
    event_report_aggregate (where: {measure_metric_id: {_eq: ${MEASURE_METRICS.DOWNTIME}}, end_date: {_is_null: true}, machine_center_id: {_eq: $machineCenterId}}){
      aggregate {
        count
      }
    }
  } 
`;

export const getExistingEvents = gql`
    query getExistingEvents($machineCenterId: Int!) {
        event_report(where: { machine_center_id: { _eq: $machineCenterId } }, limit: 1) {
            event_report_id
        }
    }
`;
